/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { UseFormSetValue, useForm } from 'react-hook-form';
import cn from 'classnames';
import {
  useState,
  useEffect,
  KeyboardEvent,
  useRef,
  MouseEventHandler,
} from 'react';
import { Input, InputProps } from '../Input/Input';

import styles from './Dropdown.module.scss';
import { Button } from '../Button/Button';
import { Icon } from '../Icon';

export interface DropdownItemsProps {
  value: string;
  placeholder: string;
}

interface DropdownProps extends Omit<InputProps, 'defaultValue'> {
  SetValue: UseFormSetValue<any>;
  items: DropdownItemsProps[];
  defaultValue?: DropdownItemsProps;
  haveSearchBar?: boolean;
  onChangeSearch?: (value: string) => void;
}

export const Dropdown = ({
  SetValue,
  classname,
  items,
  name,
  errorMessage,
  label,
  isRequired,
  defaultValue = undefined,
  haveSearchBar = false,
  onChangeSearch = () => {},
  ...inputProps
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<
    DropdownItemsProps | undefined
  >(defaultValue);
  const valuesContainer = useRef<HTMLUListElement>(undefined);
  const dropdownContainer = useRef<HTMLDivElement>(undefined);

  const { register } = useForm<{ search: string }>();

  const onClick: MouseEventHandler<HTMLElement> = (event) => {
    if (event.clientX !== 0 && event.clientY !== 0) setIsOpen((open) => !open);
  };

  const putSelectedItem = (item: DropdownItemsProps) => {
    setSelectedItem(item);
    SetValue(name, item?.value);
    setIsOpen(false);
  };

  const onKeyDownDropdown = (data: KeyboardEvent) => {
    switch (data.code) {
      case 'Enter':
        setIsOpen((open) => !open);
    }
  };

  useEffect(() => {
    if (inputProps?.onChange) {
      inputProps?.onChange(selectedItem);
    }
  }, [selectedItem]);

  const onKeyDownDropdownItem = (
    data: KeyboardEvent,
    item?: DropdownItemsProps
  ) => {
    switch (data.code) {
      case 'Tab':
        data.preventDefault();
        setIsOpen(false);
        dropdownContainer?.current?.focus();
        break;
      case 'Enter':
        data.preventDefault();
        putSelectedItem(item);
        dropdownContainer?.current?.focus();
        break;
      case 'ArrowDown':
        data.preventDefault();
        if (document.activeElement.parentNode === valuesContainer.current) {
          if (document.activeElement.nextElementSibling !== null) {
            (document.activeElement.nextElementSibling as HTMLElement)?.focus();
          } else {
            (valuesContainer.current.firstChild as HTMLElement)?.focus();
          }
        } else {
          (valuesContainer.current.children[0] as HTMLElement)?.focus();
        }
        break;
      case 'ArrowUp':
        if (document.activeElement.parentNode === valuesContainer.current) {
          if (document.activeElement.previousSibling !== null) {
            (
              document.activeElement.previousElementSibling as HTMLElement
            )?.focus();
          } else {
            (valuesContainer.current.lastChild as HTMLElement)?.focus();
          }
        } else {
          (valuesContainer.current.children[0] as HTMLElement)?.focus();
        }
    }
  };

  useEffect(() => {
    if (isOpen) {
      (valuesContainer.current.firstChild as HTMLDivElement)?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    SetValue(name, defaultValue?.placeholder);
  }, [defaultValue]);

  const renderItems = () => {
    return items?.map((item) => {
      return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <li
          className={styles['item-container']}
          key={`${item.placeholder}-${Math.random()}`}
          onKeyDown={(e) => onKeyDownDropdownItem(e, item)}
          onBlur={(event) => {
            if (
              event?.relatedTarget?.parentElement !== valuesContainer.current &&
              event?.relatedTarget?.parentElement !==
                dropdownContainer.current &&
              event?.relatedTarget?.parentElement?.parentElement !==
                dropdownContainer.current
            ) {
              setIsOpen(false);
            }
          }}
          tabIndex={-1}
          onClick={() => {
            putSelectedItem(item);
          }}
        >
          <div className={styles['dropdown-item']}>{item?.placeholder}</div>
          {selectedItem?.value === item.value && (
            <Icon type="checked-thin" alt="checked" />
          )}
        </li>
      );
    });
  };

  return (
    <div className={cn(styles.dropdown, classname)}>
      <div className={styles['dropdown-input']}>
        {label && (
          <label htmlFor={name} tabIndex={-1}>
            {label}
            {isRequired && <span className={styles.required}>*</span>}
          </label>
        )}
        <div
          onClick={inputProps.readOnly ? () => {} : onClick}
          onKeyDown={onKeyDownDropdown}
          className={cn(
            styles['dropdown-container'],
            inputProps.readOnly && styles['read-only']
          )}
          ref={dropdownContainer}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
        >
          <Input
            errorMessage={errorMessage}
            name={name}
            {...inputProps}
            classname={styles['hide-input']}
            readOnly
            tabIndex={-1}
            defaultValue={defaultValue?.placeholder}
          />
          <Input
            errorMessage={errorMessage}
            name={name}
            classname={styles.input}
            readOnly
            tabIndex={-1}
            placeholder={selectedItem?.placeholder || inputProps?.placeholder}
          />
          <Button
            className={cn(styles.icon, isOpen && styles.open)}
            icon="right-arrow"
            iconPlace="only-icon"
            tabIndex={-1}
          />
        </div>
      </div>
      {isOpen && (
        <ul ref={valuesContainer} className={styles['values-container']}>
          {haveSearchBar && (
            <input
              className={cn(styles.search)}
              type="text"
              onKeyDown={(e) => onKeyDownDropdownItem(e)}
              tabIndex={-1}
              {...register('search')}
              onBlur={(event) => {
                if (
                  event?.relatedTarget?.parentElement !==
                    valuesContainer.current &&
                  event?.relatedTarget?.parentElement !==
                    dropdownContainer.current &&
                  event?.relatedTarget?.parentElement?.parentElement !==
                    dropdownContainer.current
                ) {
                  setIsOpen(false);
                }
              }}
              name="search"
              placeholder="Buscar"
            />
          )}
          {renderItems()}
        </ul>
      )}
      <span>{errorMessage}</span>
    </div>
  );
};
